/**
 * Agent Performance (BE-14d-1)
 *
 * Per agent (the task's assignee) over invoices finalised in the range: tasks assigned and completed, lines invoiced,
 * revenue, profit and commission earned. Lines with no assigned task come last as "Unassigned".
 * Route: /reports/agent-performance
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Grid, TextField } from '@mui/material';
import ReportPage, { formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const AgentPerformance = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [to, setTo] = useState(toYmd(new Date()));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getAgentPerformance({ from, to }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load agent performance'));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const t = report?.totals;
  const columns = [
    { key: 'name', label: 'Agent', render: (r) => (r.userId ? r.name : `${r.name} (no assigned task)`) },
    { key: 'tasksAssigned', label: 'Tasks assigned', align: 'right', total: (x) => x.tasksAssigned },
    { key: 'tasksCompleted', label: 'Tasks completed', align: 'right', total: (x) => x.tasksCompleted },
    { key: 'linesInvoiced', label: 'Lines invoiced', align: 'right', total: (x) => x.linesInvoiced },
    { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue), total: (x) => formatCurrency(x.revenue) },
    { key: 'profit', label: 'Profit', align: 'right', render: (r) => formatCurrency(r.profit), total: (x) => formatCurrency(x.profit) },
    { key: 'commission', label: 'Commission', align: 'right', render: (r) => formatCurrency(r.commission), total: (x) => formatCurrency(x.commission) },
  ];

  const agents = (report?.rows || []).filter((r) => r.userId).length;
  const cards = t ? [
    { label: 'Agents', value: agents },
    { label: 'Tasks completed', value: t.tasksCompleted },
    { label: 'Revenue', value: formatCurrency(t.revenue), tone: 'good' },
    { label: 'Profit', value: formatCurrency(t.profit), tone: Number(t.profit) < 0 ? 'bad' : 'good' },
    { label: 'Commission', value: formatCurrency(t.commission) },
  ] : [];

  return (
    <ReportPage
      title="Agent Performance"
      subtitle={`By the agent assigned to each task · invoices finalised ${formatDate(from)} – ${formatDate(to)}`}
      exportType="agent-performance" exportParams={{ from, to }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.rows || []} rowKey={(r) => r.userId || 'unassigned'}
      rowSx={(r) => (r.userId ? {} : { bgcolor: 'action.hover', '& td, & p': { fontStyle: 'italic', color: 'text.secondary' } })}
      totals={t} totalsLabel="TOTAL"
      emptyMessage="No invoice was finalised in this range."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Finalised from" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Finalised to" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default AgentPerformance;
